import { UAParser } from 'ua-parser-js'
import { useSpreadInfoStore, useUserStore } from '@/stores'
import { $getDeviceId } from '@/helpers'
import { texts } from '@/config/brands'
import i18n, { LOCALE_MAP } from '@/i18n'

const parser = new UAParser()
/**
 * Mode
 * `debug`: only output to console.warn
 * `realtime`: send track event every time
 */
let mode: 'debug' | 'realtime' = 'debug'
if (import.meta.env.MODE === 'development')
  mode = 'realtime'
else
  mode = 'realtime'

const payload: Record<string, any> = {
  app: 'interactive-fiction',
  cm: {
    versionCode: __APP_VERSION__,
    platform: '3',
    content_type: '6',
    contentType: '6',
    client_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    clientGroupName: texts?.clientGroupName,
    language: LOCALE_MAP.en,
    ua: window.navigator.userAgent,
    brand: parser.getDevice().vendor, // 手机品牌
    model: parser.getDevice().model, // 手机型号
    hw: `${window.innerHeight}X${window.innerWidth}`, // heightXwidth,屏幕宽高

    member_code: '',
    member_id: '',
    equipmentId: '',
  },
  et: [],
}

function tracking(et: any) {
  // 防御性编程：如果 Pinia 还未初始化，避免访问 store
  try {
    const userStore = useUserStore()
    const spreadInfoStore = useSpreadInfoStore()
    payload.cm.member_code = userStore.code
    payload.cm.member_id = userStore.memberId
    payload.cm.client_t = Date.now()
    payload.cm.equipmentId = $getDeviceId()
    // 公参 - 广告信息
    payload.cm.landingPageType = spreadInfoStore.$state.landingPageType
    payload.cm.utm_campaign_id = spreadInfoStore.$state.utm_campaign_id
    payload.cm.utm_medium = spreadInfoStore.$state.utm_medium
    payload.cm.utm_adset_id = spreadInfoStore.$state.utm_adset_id
    payload.cm.utm_ad_id = spreadInfoStore.$state.utm_ad_id
    payload.cm.pId = spreadInfoStore.$state.pId
    payload.cm.language = LOCALE_MAP[i18n.locale as keyof typeof LOCALE_MAP] || LOCALE_MAP.en
    payload.cm.adDomainId = spreadInfoStore.$state.adDomainId
    payload.cm.bizAdId = spreadInfoStore.$state.bizAdId
    payload.cm.adChannel = spreadInfoStore.$state.adChannel
  }
  catch (error) {
    // Store 未初始化时，使用默认值
    console.warn('Store not initialized yet, using default values for tracking')
    payload.cm.member_code = ''
    payload.cm.member_id = ''
    payload.cm.client_t = Date.now()
    payload.cm.equipmentId = $getDeviceId()
  }

  const data = {
    app: payload.app,
    cm: payload.cm,
    et,
  }

  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
  // sendBeacon 无法设置 header
  // if (typeof navigator.sendBeacon === 'function') {
  //   navigator.sendBeacon(`${import.meta.env.VITE_TRACK_DOMAIN}/tracking/report`, new Blob([JSON.stringify(data)], { type: 'application/json' }))
  //   return
  // }

  for (const item of et)
    sendToRumSDK(item)

  try {
    fetch(`${location.origin}/${import.meta.env.VITE_TRACK_PREFIX}/tracking/report`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        'platform': '3',
        'timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        'equipmentId': $getDeviceId() || '',
        'language': LOCALE_MAP[i18n.locale as keyof typeof LOCALE_MAP] || LOCALE_MAP.en,
      },
      keepalive: true,
    })
  }
  catch (error) {
    console.error('Track fetch error:', error)
  }
}

export async function track(_name: string, _params: Record<string, any>) {}

export interface RumCustomParams {
  type: string
  name: string
  group?: string
  value?: number
  properties?: Record<string, any>
}

type RumTransformer = (event: { en: string; kv: Record<string, any> }) => RumCustomParams | null

const rumTransformers: Array<{
  match: (name: string) => boolean
  transform: RumTransformer
}> = []

export function registerRumTransformer(
  match: (name: string) => boolean,
  transform: RumTransformer,
) {
  rumTransformers.push({ match, transform })
}

function sendToRumSDK(item: { en: string; kv: Record<string, any> }) {
  if (!window.RumSDK)
    return
  try {
    const transformer = rumTransformers.find(t => t.match(item.en))
    const params = transformer
      ? transformer.transform(item)
      : { type: 'event', name: item.en, properties: item.kv }
    if (params)
      window.RumSDK.default.sendCustom(params as any)
  }
  catch (error) {
    console.error('RumSDK sendCustom error:', error)
  }
}

export async function newTrack(name: string, params: Record<string, any>) {
  let et: any = []
  et.push({
    ett: Date.now(),
    en: name,
    kv: params,
  })

  if (mode === 'debug') {
    console.warn(`[track] ${name}: `, { payload, params })
    return
  }

  tracking(et)
  et = []
  payload.et = []
}

let _timer = null
let isTrackingStarted = false
const trackDelay = 5000
let asyncEt = [] as any
export async function asyncTrack(name: string, params: Record<string, any>) {
  asyncEt.push({
    ett: Date.now(),
    en: name,
    kv: params,
  })

  if (mode === 'debug') {
    console.warn(`[track] ${name}: `, { payload, params })
    return
  }

  if (!isTrackingStarted) {
    isTrackingStarted = true
    _timer = setTimeout(async () => {
      tracking(asyncEt)
      asyncEt = []
      isTrackingStarted = false
      _timer = null
    }, trackDelay)
  }
}
